<template>
  <div class="tw:rounded-xl tw:flex tw:flex-col">
    <div class="tw:flex">
      <!-- Calendar Section -->

      <div
        @click="setActiveSection('calendar')"
        @focusin="setActiveSection('calendar')"
        class="tw:rounded-tl-xl tw:transition-all tw:duration-300 tw:border tw:border-gray-300"
        :class="[
          // Mobile logic (< 1023px)
          isMobile && boostStepCommission && calendarExpandedMobile
            ? 'tw:w-[calc((100%-64px)/2)]'
            : isMobile && calendarExpandedMobile
              ? 'tw:w-full'
              : isMobile && boostStepCommission && !calendarExpandedMobile
                ? 'tw:w-[64px]'
                : isMobile && !boostStepCommission
                  ? 'tw:w-1/2'
                  : '',

          // Desktop logic (≥ 1024px)
          !isMobile && isEditingMode && !isEmptySelection
            ? 'tw:w-2/3'
            : !isMobile && boostStepCommission
              ? 'tw:w-1/3'
              : !isMobile && !boostStepCommission
                ? 'tw:w-2/3'
                : '',

          // Highlighting
          (!isEditingMode || isEmptySelection) && isCalendarHighlighted
            ? 'tw:shadow-[inset_2px_0_0_0_#0079C2,inset_-2px_0_0_0_#0079C2,inset_0_2px_0_0_#0079C2,inset_0_-2px_0_0_#0079C2] !tw:border-0'
            : '',
        ]"
      >
        <div
          class="tw:flex tw:items-center tw:p-3 tw:h-[48px]"
          :class="[
            isMobile ? 'tw:cursor-pointer' : '',
            isMobile && !calendarExpandedMobile && boostStepCommission ? 'tw:justify-center' : '',
            !isEditingMode && isCalendarHighlighted ? 'tw:border-b-0' : 'tw:border-b tw:border-b-gray-300',
          ]"
        >
          <button @click="toggleCalendar()" :disabled="!isMobile" class="tw:flex tw:gap-1 tw:items-center">
            <CalendarIcon class="tw:size-6" />
            <p
              class="tw:text-base tw:text-gray-800 tw:font-bold"
              :class="isMobile && boostStepCommission && !calendarExpandedMobile ? 'tw:hidden' : 'tw:flex'"
            >
              {{ $t('rc_smartboost_calendar') }}
            </p>
          </button>
        </div>
        <div v-show="!isMobile || !boostStepCommission || calendarExpandedMobile" class="tw:px-[2px]">
          <SmartBoostCalendar
            :showTwoMonths="showTwoMonths"
            :isEditing="isEditingMode"
            :hasReachedLimit="hasReachedLimit"
            @selectionComplete="onDateSelectionComplete"
            @switchToCreationMode="handleSwitchToCreation"
          />
          <div class="tw:w-full tw:px-4 tw:mx-auto tw:mb-4">
            <SmartBoostNotification :text="notificationText" :type="infoType" />
          </div>
        </div>
      </div>

      <!-- Select Dates Section -->
      <div
        @click="setActiveSection('dates')"
        @focusin="setActiveSection('dates')"
        v-show="!isMobile || (!calendarExpandedMobile && !boostStepCommission) || boostStepCommission"
        class="tw:transition-all tw:duration-300 tw:border tw:border-l-0 tw:border-gray-300"
        :class="[
          // Mobile logic (< 1024px)
          isMobile && boostStepCommission
            ? 'tw:w-[calc((100%-64px)/2)]'
            : isMobile && !boostStepCommission
              ? 'tw:w-1/2'
              : '',

          // Desktop logic (≥ 1024px) - Default
          !isMobile && isEditingMode && !isEmptySelection
            ? 'tw:w-1/3'
            : !isMobile && !boostStepCommission && (getSelectedDatesArray.length > 0 || isEmptySelection)
              ? 'tw:w-[calc(100%-66.67%-64px)]'
              : !isMobile && !boostStepCommission && !isEditingMode
                ? 'tw:w-1/3'
                : !isMobile && boostStepCommission
                  ? 'tw:w-1/3'
                  : '',

          // Corner rounding
          getSelectedDatesArray.length === 0 &&
          !isEmptySelection &&
          !boostStepCommission &&
          !showCommissionSection &&
          !isEditingMode
            ? 'tw:rounded-tr-xl'
            : '',

          // Highlighting
          (!isEditingMode || isEmptySelection) && isDatesHighlighted
            ? 'tw:shadow-[inset_-2px_0_0_0_#0079C2,inset_0_2px_0_0_#0079C2,inset_0_-2px_0_0_#0079C2] tw:p-[2px] !tw:border-0'
            : '',
        ]"
      >
        <div
          class="tw:flex tw:items-center tw:gap-1 tw:p-3 tw:h-[48px]"
          :class="[
            !isEditingMode && (isDatesHighlighted || (getSelectedDatesArray.length > 0 && !boostStepCommission))
              ? 'tw:border-b-0'
              : boostStepCommission
                ? 'tw:border-b tw:border-b-gray-300'
                : 'tw:border-b tw:border-b-gray-300',
          ]"
        >
          <CheckCircle
            class="tw:size-[18px]"
            :class="getSelectedDatesArray.length > 0 ? 'tw:opacity-100' : 'tw:opacity-50'"
          />
          <p
            class="tw:text-base tw:text-gray-800 tw:font-bold tw:block"
            :class="getSelectedDatesArray.length > 0 ? 'tw:opacity-100' : 'tw:opacity-50'"
          >
            {{ isEditingMode ? $t('rc_smartboost_dates_boosted') : $t('rc_smartboost_dates_header') }}
          </p>
        </div>

        <SmartBoostSelectedDates :isEditing="isEditingMode" />
      </div>

      <div
        v-if="isEditingMode && !isEmptySelection"
        class="tw:transition-all tw:duration-300 tw:rounded-tr-xl tw:border tw:border-l-0 tw:border-gray-300 tw:w-[64px]"
      >
        <div class="tw:flex tw:items-center tw:justify-center tw:p-3 tw:h-[48px] tw:border-b tw:border-b-gray-300">
          <RevenueIcon class="tw:size-6 tw:opacity-50" />
        </div>
      </div>

      <!-- Commission Section (Creation Mode) -->
      <div
        @click="setActiveSection('commission')"
        @focusin="setActiveSection('commission')"
        v-if="
          (showCommissionSection || boostStepCommission || isEmptySelection) && (!isEditingMode || isEmptySelection)
        "
        v-show="!isMobile || (!calendarExpandedMobile && !boostStepCommission) || boostStepCommission"
        class="tw:transition-all tw:duration-300 tw:rounded-tr-xl"
        :class="[
          !boostStepCommission
            ? 'tw:w-[64px]'
            : isMobile && calendarExpandedMobile
              ? 'tw:w-[64px]'
              : isMobile && commissionExpandedMobile
                ? 'tw:w-[calc((100%-64px)/2)]'
                : isMobile
                  ? 'tw:w-[calc((100%-64px)/2)]'
                  : 'tw:w-1/3',
          (!isEditingMode || isEmptySelection) && isCommissionHighlighted
            ? 'tw:shadow-[inset_2px_0_0_0_#0079C2,inset_-2px_0_0_0_#0079C2,inset_0_2px_0_0_#0079C2,inset_0_-2px_0_0_#0079C2] tw:p-[2px] '
            : 'tw:border tw:border-gray-300',
          isMobile ? 'tw:border-l-0' : '',
        ]"
      >
        <div
          class="tw:flex tw:items-center tw:p-3 tw:h-[48px]"
          :class="[
            !boostStepCommission || (isMobile && calendarExpandedMobile) ? 'tw:justify-center' : '',
            isMobile && boostStepCommission ? 'tw:cursor-pointer' : '',
            !isEditingMode && isCommissionHighlighted ? 'tw:border-b-0' : 'tw:border-b tw:border-b-gray-300',
          ]"
        >
          <button
            @click="toggleCommission"
            :disabled="!(isMobile && boostStepCommission)"
            class="tw:flex tw:gap-1"
            :class="boostStepCommission ? 'tw:opacity-100' : 'tw:opacity-50'"
          >
            <RevenueIcon class="tw:size-6" />
            <p
              class="tw:text-base tw:text-gray-800 tw:font-bold"
              :class="
                boostStepCommission && (!isMobile || !calendarExpandedMobile) && (!isMobile || commissionExpandedMobile)
                  ? 'tw:flex'
                  : 'tw:hidden'
              "
              v-if="boostStepCommission"
            >
              {{ $t('rc_smartboost_commission_header') }}
            </p>
          </button>
        </div>
        <SmartBoostCommissions v-if="boostStepCommission" v-show="!isMobile || commissionExpandedMobile" />
      </div>
    </div>

    <!-- Footer: Edit Mode (when not in empty selection) -->
    <div
      v-if="isEditingMode && !isEmptySelection"
      class="tw:flex tw:border tw:rounded-b-xl tw:border-t-0 tw:border-gray-300 tw:py-4 tw:justify-center tw:w-full tw:items-center"
    >
      <MainButton
        :disabled="hasReachedLimit"
        :dataTestId="'smart-boost-add-new-boost-button'"
        @click="handleOpenEmptySelection"
      >
        <span>{{ '+ ' }}</span>
        <span>{{ $t('rc_smartboost_add_cta') }}</span>
      </MainButton>
    </div>

    <!-- Footer: Creation Mode (no dates selected) -->
    <div
      v-else-if="getSelectedDatesArray.length === 0 && !isEmptySelection"
      class="tw:flex tw:border tw:rounded-b-xl tw:border-t-0 tw:border-gray-300 tw:py-4 tw:justify-center tw:w-full tw:items-center"
    >
      <MainButton
        :disabled="hasReachedLimit"
        :dataTestId="'smart-boost-add-new-boost-button'"
        @click="handleOpenEmptySelection"
      >
        <span>{{ '+ ' }}</span>
        <span>{{ $t('rc_smartboost_add_cta') }}</span>
      </MainButton>
    </div>

    <!-- Footer: Active Selection (dates selected or in progress) -->
    <div
      v-else
      class="tw:flex tw:border tw:rounded-b-xl tw:border-t-0 tw:border-gray-300 tw:p-4 tw:justify-between tw:w-full tw:items-center"
    >
      <GhostButton :dataTestId="'smart-boost-footer-cancel-button'" @click="handleCancel">{{
        $t('rc_smartboost_cancel_cta')
      }}</GhostButton>
      <MainButton
        :dataTestId="'smart-boost-footer-primary-button'"
        class="tw:flex tw:items-center tw:gap-x-2"
        :disabled="isButtonDisabled"
        @click="nextStepAction"
      >
        <span>{{
          boostStepCommission && selectedCommission ? $t('rc_smartboost_next_cta') : $t('rc_smartboost_commission_cta')
        }}</span>
        <svg width="7" height="12" viewBox="0 0 7 12" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M1 11L6 6" stroke="#fff" stroke-width="2" stroke-linecap="round" />
          <path d="M1 1L6 6" stroke="#fff" stroke-width="2" stroke-linecap="round" />
        </svg>
      </MainButton>
    </div>
  </div>
</template>

<script setup>
import { computed, nextTick, ref, watch } from 'vue'

import CalendarIcon from '@/components/icons/CalendarIcon.svg?component'
import SmartBoostCalendar from '@/components/rateConnect/smartBoost/SmartBoostCalendar.vue'
import SmartBoostSelectedDates from '@/components/rateConnect/smartBoost/SmartBoostSelectedDates.vue'
import SmartBoostNotification from '@/components/rateConnect/smartBoost/SmartBoostNotification.vue'
import CheckCircle from '@/components/icons/CheckCircle.svg?component'
import MainButton from '@/components/base/buttons/MainButton.vue'
import GhostButton from '@/components/base/buttons/GhostButton.vue'
import RevenueIcon from '@/components/icons/RevenueIcon.svg?component'
import SmartBoostCommissions from '@/components/rateConnect/smartBoost/SmartBoostCommissions.vue'

import { useSmartBoost } from '@/components/rateConnect/smartBoost/useSmartBoost'
import { useIsMobile } from '@/composables/useIsMobile'
import { useActiveBoosts, useBoostLevels, useCampaign } from '../queries'
import { selectedAccommodationId } from '@/layouts/queries'

import appAnalytics from '@/utils/tracking'

const {
  selectedCommission,
  getSelectedDatesArray,
  openEmptySelection,
  isEmptySelection,
  cancelEmptySelection,
  toggleConfirmBoostModal,
  clearDates,
  countTotalActiveBoostRanges,
  getSelectedDatesFormatted,
  isEditingMode,
  activeBoostDates,
  switchToCreationMode,
  resetCalendarMonth,
  setRange,
  selectCommission,
  consumeSmartBoostPrefill,
} = useSmartBoost()

// Constants
const MAX_BOOST_RANGES = 50

const { isMobile } = useIsMobile(1024)
const boostStepCommission = ref(false)
const calendarExpandedMobile = ref(false)
const commissionExpandedMobile = ref(false)
const activeSection = ref('')
const showCommissionSection = ref(false)
const selectionCompleted = ref(false)

const { campaignsData, currentCommissionLevel, isCampaignsDataInitialLoading } = useCampaign(selectedAccommodationId)
const { activeBoosts, isActiveBoostsLoading } = useActiveBoosts(selectedAccommodationId)
const { boostLevels, isLoading: isBoostLevelsLoading } = useBoostLevels()
const status = computed(() => campaignsData.value?.status)

const resetCreationModeState = () => {
  boostStepCommission.value = false
  calendarExpandedMobile.value = false
  commissionExpandedMobile.value = false
  showCommissionSection.value = false
  selectionCompleted.value = false
  activeSection.value = ''
  selectedCommission.value = null
}

// Count existing active ranges from API
const currentActiveBoostRanges = computed(() => {
  return countTotalActiveBoostRanges(activeBoosts.value)
})

// Count total: existing ranges + newly selected ranges (consecutive groups)
const totalBoostRanges = computed(() => {
  const newRanges = isEmptySelection.value ? getSelectedDatesFormatted.value.length : 0
  return currentActiveBoostRanges.value + newRanges
})

// Check if limit reached
const hasReachedLimit = computed(() => {
  return totalBoostRanges.value >= MAX_BOOST_RANGES
})

const showTwoMonths = computed(() => {
  if (isMobile.value) return false

  if (isEditingMode.value && !isEmptySelection.value) {
    return true
  }

  if (boostStepCommission.value) {
    return false
  }

  return true
})

const isCalendarHighlighted = computed(() => {
  if (boostStepCommission.value) return false
  if (isEmptySelection.value) return true
  if (isEditingMode.value && !isEmptySelection.value) return false

  if (isEmptySelection.value) return true
  if (activeSection.value === 'commission') return false

  return activeSection.value === 'calendar' || activeSection.value === 'dates'
})

const isDatesHighlighted = computed(() => {
  if (isEditingMode.value && !isEmptySelection.value) return false

  if (boostStepCommission.value) return false

  if (getSelectedDatesArray.value.length > 0) return true

  if (activeSection.value === 'commission') return false
  return false
})

const isCommissionHighlighted = computed(() => {
  if (isEditingMode.value && !isEmptySelection.value) return false

  if (selectedCommission.value !== null) return true

  if (boostStepCommission.value) return true

  return activeSection.value === 'commission'
})

const handleSwitchToCreation = () => {
  switchToCreationMode()
  resetCreationModeState()
}

const setActiveSection = section => {
  if (!isEditingMode.value || isEmptySelection.value) {
    activeSection.value = section
  }
}

const infoType = computed(() => {
  if (
    (status.value === 'PAUSED' || status.value === 'PAUSED_UNTIL') &&
    getSelectedDatesArray.value.length === 0 &&
    !isEmptySelection.value
  ) {
    return 'warning'
  } else if (hasReachedLimit.value) {
    return 'error'
  } else return 'info'
})

const notificationText = computed(() => {
  if (
    getSelectedDatesArray.value.length === 0 &&
    (status.value === 'PAUSED' || status.value === 'PAUSED_UNTIL') &&
    !isEmptySelection.value
  ) {
    return 'rc_smartboost_paused_campaign_info'
  }

  if (hasReachedLimit.value) {
    return 'rc_smartboost_error_message'
  }

  if (isEditingMode.value && !isEmptySelection.value) {
    if (status.value === 'PAUSED' || status.value === 'PAUSED_UNTIL') {
      return 'rc_smartboost_paused_campaign_info'
    }
    return 'rc_smartboost_dates_info'
  }

  if (getSelectedDatesArray.value.length === 0) {
    return 'rc_smartboost_calendar_start'
  }

  return 'rc_smartboost_calendar_info'
})

const toggleCalendar = () => {
  calendarExpandedMobile.value = !calendarExpandedMobile.value
  if (calendarExpandedMobile.value && commissionExpandedMobile.value) {
    commissionExpandedMobile.value = false
  }
  setActiveSection('calendar')
}

const handleOpenEmptySelection = () => {
  activeSection.value = 'calendar'
  openEmptySelection()
}

const toggleCommission = () => {
  commissionExpandedMobile.value = !commissionExpandedMobile.value
  if (commissionExpandedMobile.value && calendarExpandedMobile.value) {
    calendarExpandedMobile.value = false
  }
  setActiveSection('commission')
}

const nextStepAction = () => {
  if (getSelectedDatesArray.value.length > 0 && !boostStepCommission.value) {
    openSelectCommission()
    appAnalytics.track(appAnalytics.events.RC_SMART_BOOST_DATES_SELECTED, { item_id: selectedAccommodationId.value })
  } else if (boostStepCommission.value && selectedCommission.value) {
    appAnalytics.track(appAnalytics.events.RC_SMART_BOOST_COMMISSION_SELECTED, {
      item_id: selectedAccommodationId.value,
    })
    toggleConfirmBoostModal('open')
  }
}

const isButtonDisabled = computed(() => {
  if (isEmptySelection.value && getSelectedDatesArray.value.length === 0) {
    return true
  } else if (getSelectedDatesArray.value.length === 0 && !boostStepCommission.value) {
    return true
  } else if (boostStepCommission.value && selectedCommission.value === null) {
    return true
  } else {
    return false
  }
})

const openSelectCommission = () => {
  if (getSelectedDatesArray.value.length > 0) {
    boostStepCommission.value = true
    activeSection.value = 'commission'
    if (isMobile.value) {
      calendarExpandedMobile.value = false
      commissionExpandedMobile.value = true
    }
  }
}

const handleCancel = () => {
  resetCreationModeState()
  resetCalendarMonth()
  cancelEmptySelection()

  if (activeBoostDates.value.size === 0) {
    clearDates()
  }
}
watch(
  getSelectedDatesArray,
  newValue => {
    if (newValue.length === 0) {
      boostStepCommission.value = false
      if (!isEmptySelection.value) {
        activeSection.value = ''
      }
      showCommissionSection.value = false
      selectionCompleted.value = false
      if (isMobile.value) {
        calendarExpandedMobile.value = false
        commissionExpandedMobile.value = false
      }

      // if (isEmptySelection.value) {
      //   if (activeBoostDates.value.size > 0) {
      //     cancelEmptySelection()
      //   } else {
      //     cancelEmptySelection()
      //     clearDates()
      //   }
      // }
    }
  },
  { deep: true }
)

watch(isMobile, newValue => {
  if (!newValue) {
    calendarExpandedMobile.value = false
    commissionExpandedMobile.value = false
  }
})

watch(isEditingMode, newValue => {
  if (newValue && !isEmptySelection.value) {
    // Switched to editing mode (e.g., after boost creation)
    resetCreationModeState()
  }
})

const onDateSelectionComplete = () => {
  if (getSelectedDatesArray.value.length > 0) {
    selectionCompleted.value = true
    setTimeout(() => {
      showCommissionSection.value = true
    }, 50)
  }
}

const applyPrefill = () => {
  const prefill = consumeSmartBoostPrefill()
  if (!prefill?.startDate || !prefill?.endDate) return

  const startTs = new Date(`${prefill.startDate}T00:00:00`).getTime()
  const endTs = new Date(`${prefill.endDate}T00:00:00`).getTime()
  if (Number.isNaN(startTs) || Number.isNaN(endTs)) return

  if (isEditingMode.value) {
    openEmptySelection()
  } else {
    switchToCreationMode()
  }

  setRange(startTs, endTs)

  if (getSelectedDatesArray.value.length > 0) {
    selectionCompleted.value = true
    showCommissionSection.value = true

    const commissionLevel = prefill.commission != null ? Number(prefill.commission) : NaN
    const allowedLevels =
      boostLevels.value?.filter(level => level > currentCommissionLevel.value) ?? []

    if (!Number.isNaN(commissionLevel) && allowedLevels.includes(commissionLevel)) {
      selectCommission(commissionLevel)
      openSelectCommission()
    } else {
      selectCommission(null)
      activeSection.value = 'dates'
    }
  }
}

const isPrefillDataReady = computed(
  () => !isActiveBoostsLoading.value && !isBoostLevelsLoading.value && !isCampaignsDataInitialLoading.value
)

watch(
  isPrefillDataReady,
  ready => {
    if (ready) {
      nextTick(() => applyPrefill())
    }
  },
  { immediate: true }
)
</script>
